import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import {
  listInstantlyMetricsAsAdmin,
  type InstantlyCampaignMetric,
  type InstantlyDayMetric,
  type InstantlyPositiveReply,
  type InstantlySenderMetric,
} from '@/services/admin';
import {
  changeAccountPassword,
  deleteMySchedule,
  getAccountSettings,
  listMyScheduleRuns,
  type IntegrationField,
  setMyGhlConnection,
  testMyGhlConnection,
  updateMySchedule,
  updateAccountProfile,
} from '@/services/account';
import { listInstantlyCampaignOptions, type InstantlyCampaignOption } from '@/services/research';

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatHour(hour: number, minute = 0) {
  const h = Number.isFinite(hour) ? hour : 0;
  const m = Number.isFinite(minute) ? minute : 0;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const normalized = h % 12 === 0 ? 12 : h % 12;
  return `${normalized}:${String(m).padStart(2, '0')} ${suffix}`;
}

function IntegrationRow({ label, value }: { label: string; value: IntegrationField | undefined }) {
  const isValid = Boolean(value?.valid);
  const hasValue = Boolean(value?.hasValue);
  const statusText = isValid ? 'Valid' : hasValue ? 'Invalid' : 'Missing';
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-900/50 px-4 py-3">
      <div>
        <div className="text-sm text-white">{label}</div>
        <div className="text-xs text-gray-500">{value?.maskedValue || 'Not set'}</div>
      </div>
      <div className={`inline-flex items-center gap-2 text-xs font-semibold ${isValid ? 'text-green-400' : 'text-red-400'}`}>
        {isValid ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {statusText}
      </div>
    </div>
  );
}

function normalizeCampaignIds(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

type AccountTab = 'settings' | 'email_data';

export function AccountSettings() {
  const userEmail = useAppStore((s) => s.userEmail);
  const adminToken = useAppStore((s) => s.adminToken);
  const setCredits = useAppStore((s) => s.setCredits);
  const setContactCredits = useAppStore((s) => s.setContactCredits);

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [account, setAccount] = useState<any>(null);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [integrations, setIntegrations] = useState<Record<string, IntegrationField>>({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ghlAccountName, setGhlAccountName] = useState('');
  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlPit, setGhlPit] = useState('');
  const [ghlTestContactName, setGhlTestContactName] = useState('Profitibull Support');
  const [ghlTestContactEmail, setGhlTestContactEmail] = useState('support@profitibull.com');
  const [ghlTestCompanyName, setGhlTestCompanyName] = useState('Profitibull');
  const [ghlSaving, setGhlSaving] = useState(false);
  const [ghlTesting, setGhlTesting] = useState(false);
  const [ghlStatus, setGhlStatus] = useState<string | null>(null);
  const [ghlTestResultJson, setGhlTestResultJson] = useState<string>('');
  const [activeTab, setActiveTab] = useState<AccountTab>('settings');

  const [emailDataLoading, setEmailDataLoading] = useState(false);
  const [emailDataStatus, setEmailDataStatus] = useState<string | null>(null);
  const [emailCampaignOptions, setEmailCampaignOptions] = useState<InstantlyCampaignOption[]>([]);
  const [emailFilterCampaignId, setEmailFilterCampaignId] = useState('');
  const [emailFilterSenderEmail, setEmailFilterSenderEmail] = useState('');
  const [emailFilterStart, setEmailFilterStart] = useState('');
  const [emailFilterEnd, setEmailFilterEnd] = useState('');
  const [emailLimit, setEmailLimit] = useState('20000');
  const [emailCampaignRows, setEmailCampaignRows] = useState<InstantlyCampaignMetric[]>([]);
  const [emailSenderRows, setEmailSenderRows] = useState<InstantlySenderMetric[]>([]);
  const [emailDayRows, setEmailDayRows] = useState<InstantlyDayMetric[]>([]);
  const [emailPositiveReplies, setEmailPositiveReplies] = useState<InstantlyPositiveReply[]>([]);
  const [emailTotals, setEmailTotals] = useState<Record<string, number>>({});
  const [emailUniqueTotals, setEmailUniqueTotals] = useState<Record<string, number>>({});
  const [scheduleRuns, setScheduleRuns] = useState<any[]>([]);
  const [scheduleActionLoadingId, setScheduleActionLoadingId] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!userEmail || !adminToken) return;
      setLoading(true);
      setError('');
      try {
        const result = await getAccountSettings({ token: adminToken, email: userEmail });
        if (!result.success || !result.account) throw new Error(result.error || 'Failed to load account settings');
        setAccount(result.account);
        setSchedules(result.schedules || []);
        setIntegrations(result.integrations || {});
        setFirstName(result.account.firstName || '');
        setLastName(result.account.lastName || '');
        setGhlAccountName(result.account.accountName || '');
        setGhlLocationId(result.account.ghlLocationId || result.integrations?.locationId?.value || '');
        setGhlPit(result.integrations?.pitToken?.value || '');
        setCredits(Number(result.account.credits || 0));
        setContactCredits(Number(result.account.contactCredits || 0));
      } catch (e: any) {
        setError(e?.message || 'Failed to load account settings');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [adminToken, setContactCredits, setCredits, userEmail]);

  useEffect(() => {
    const run = async () => {
      if (!userEmail || !adminToken) return;
      try {
        const result = await listMyScheduleRuns({
          email: userEmail,
          token: adminToken,
          limit: 1000,
        });
        if (!result.success) return;
        setScheduleRuns(Array.isArray(result.items) ? result.items : []);
      } catch {
        setScheduleRuns([]);
      }
    };
    run();
  }, [adminToken, userEmail]);

  const assignedCampaignIds = useMemo(
    () => normalizeCampaignIds(account?.instantlyCampaignIds),
    [account?.instantlyCampaignIds]
  );
  const assignedCampaignIdsKey = useMemo(() => assignedCampaignIds.join('|'), [assignedCampaignIds]);
  const hasEmailDataAccess = assignedCampaignIds.length > 0;

  useEffect(() => {
    if (hasEmailDataAccess) return;
    if (activeTab === 'email_data') setActiveTab('settings');
    setEmailCampaignOptions([]);
    setEmailFilterCampaignId('');
  }, [activeTab, hasEmailDataAccess]);

  useEffect(() => {
    if (!hasEmailDataAccess || activeTab !== 'email_data') return;
    let cancelled = false;
    listInstantlyCampaignOptions({ userEmail })
      .then((result) => {
        if (cancelled) return;
        const allCampaigns = Array.isArray(result.campaigns) ? result.campaigns : [];
        const byId = new Map(allCampaigns.map((campaign) => [campaign.id, campaign]));
        const restricted = assignedCampaignIds.map((id) => byId.get(id) ?? { id, name: id });
        setEmailCampaignOptions(restricted);
        setEmailFilterCampaignId((current) =>
          current && restricted.some((campaign) => campaign.id === current)
            ? current
            : (restricted[0]?.id ?? '')
        );
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = assignedCampaignIds.map((id) => ({ id, name: id }));
        setEmailCampaignOptions(fallback);
        setEmailFilterCampaignId((current) =>
          current && fallback.some((campaign) => campaign.id === current)
            ? current
            : (fallback[0]?.id ?? '')
        );
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, assignedCampaignIds, assignedCampaignIdsKey, hasEmailDataAccess, userEmail]);

  const loadEmailData = async () => {
    if (!userEmail || !adminToken || !hasEmailDataAccess) return;
    const selectedCampaignId = emailFilterCampaignId.trim();
    if (!selectedCampaignId) {
      setEmailDataStatus('Select an assigned campaign first.');
      return;
    }
    if (!assignedCampaignIds.includes(selectedCampaignId)) {
      setEmailDataStatus('Selected campaign is not assigned to your account.');
      return;
    }
    setEmailDataLoading(true);
    setEmailDataStatus(null);
    try {
      const result = await listInstantlyMetricsAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        userEmail: userEmail.trim(),
        campaignId: selectedCampaignId,
        senderEmail: emailFilterSenderEmail.trim() || undefined,
        start: emailFilterStart ? new Date(emailFilterStart).toISOString() : undefined,
        end: emailFilterEnd ? new Date(`${emailFilterEnd}T23:59:59.999Z`).toISOString() : undefined,
        limit: Number(emailLimit) || 20000,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load Instantly metrics.');
      setEmailCampaignRows(Array.isArray(result.byCampaign) ? result.byCampaign : []);
      setEmailSenderRows(Array.isArray(result.bySender) ? result.bySender : []);
      setEmailDayRows(Array.isArray(result.byDay) ? result.byDay : []);
      setEmailPositiveReplies(Array.isArray(result.positiveReplies) ? result.positiveReplies : []);
      setEmailTotals((result.totals as Record<string, number>) ?? {});
      setEmailUniqueTotals((result.uniqueTotals as Record<string, number>) ?? {});
      setEmailDataStatus(
        `Loaded ${(result.sampleSize ?? 0).toLocaleString()} events for campaign ${selectedCampaignId}.`
      );
    } catch (error: any) {
      setEmailDataStatus(error?.message || 'Failed to load Instantly metrics.');
    } finally {
      setEmailDataLoading(false);
    }
  };

  const emailTopCampaigns = useMemo(
    () => [...emailCampaignRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [emailCampaignRows]
  );
  const emailTopSenders = useMemo(
    () => [...emailSenderRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [emailSenderRows]
  );
  const emailOpenRate = useMemo(() => {
    const sends = Number(emailTotals.sends ?? 0);
    const opens = Number(emailTotals.opens ?? 0);
    return sends > 0 ? (opens / sends) * 100 : 0;
  }, [emailTotals.opens, emailTotals.sends]);
  const emailClickRate = useMemo(() => {
    const sends = Number(emailTotals.sends ?? 0);
    const clicks = Number(emailTotals.clicks ?? 0);
    return sends > 0 ? (clicks / sends) * 100 : 0;
  }, [emailTotals.clicks, emailTotals.sends]);
  const emailReplyRate = useMemo(() => {
    const sends = Number(emailTotals.sends ?? 0);
    const replies = Number(emailTotals.replies ?? 0);
    return sends > 0 ? (replies / sends) * 100 : 0;
  }, [emailTotals.replies, emailTotals.sends]);

  const onSaveProfile = async () => {
    if (!userEmail || !adminToken) return;
    setSavingProfile(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateAccountProfile({ token: adminToken, email: userEmail, firstName, lastName });
      if (!res.success) throw new Error(res.error || 'Failed to update profile');
      setSuccess('Profile updated.');
    } catch (e: any) {
      setError(e?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePassword = async () => {
    if (!userEmail || !adminToken) return;
    if (!currentPassword || !newPassword) {
      setError('Enter current and new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setChangingPassword(true);
    setError('');
    setSuccess('');
    try {
      const res = await changeAccountPassword({ token: adminToken, email: userEmail, currentPassword, newPassword });
      if (!res.success) throw new Error(res.error || 'Failed to change password');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password updated.');
    } catch (e: any) {
      setError(e?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const onSaveGhlConnection = async () => {
    if (!userEmail || !adminToken) return;
    if (!ghlLocationId.trim() || !ghlPit.trim()) {
      setError('GHL Location ID and PIT token are required.');
      return;
    }
    setGhlSaving(true);
    setGhlStatus(null);
    setGhlTestResultJson('');
    setError('');
    setSuccess('');
    try {
      const res = await setMyGhlConnection({
        token: adminToken,
        email: userEmail,
        accountName: ghlAccountName.trim() || null,
        locationId: ghlLocationId.trim(),
        pit: ghlPit.trim(),
      });
      if (!res.success) throw new Error(res.error || 'Failed to save GHL connection');
      setGhlStatus('GHL connection saved.');
      setSuccess('GHL connection saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save GHL connection');
    } finally {
      setGhlSaving(false);
    }
  };

  const onTestGhlConnection = async () => {
    if (!userEmail || !adminToken) return;
    setGhlTesting(true);
    setGhlStatus(null);
    setGhlTestResultJson('');
    setError('');
    setSuccess('');
    try {
      const res = await testMyGhlConnection({
        token: adminToken,
        email: userEmail,
        locationId: ghlLocationId.trim() || undefined,
        pit: ghlPit.trim() || undefined,
        contactName: ghlTestContactName.trim() || undefined,
        contactEmail: ghlTestContactEmail.trim() || undefined,
        companyName: ghlTestCompanyName.trim() || undefined,
      });
      setGhlTestResultJson(JSON.stringify(res, null, 2));
      if (!res.success) {
        const statusDetail = res.statusCode ? ` (HTTP ${res.statusCode})` : '';
        throw new Error((res.error || 'GHL connection test failed') + statusDetail);
      }
      setGhlStatus(`GHL test passed${res.statusCode ? ` (HTTP ${res.statusCode})` : ''}.`);
      setSuccess('GHL test passed.');
    } catch (e: any) {
      setError(e?.message || 'GHL test failed');
    } finally {
      setGhlTesting(false);
    }
  };

  const sortedSchedules = useMemo(() => {
    return [...schedules].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [schedules]);

  const scheduleMetrics = useMemo(() => {
    const metrics = new Map<string, { lastPage: number | null; totalPages: number | null; creditsUsed: number | null }>();
    const byParent = new Map<string, { scheduleId: string; updatedAt: number; pages: any[] }>();
    const parseTime = (v: any) => {
      const t = Date.parse(String(v ?? ''));
      return Number.isFinite(t) ? t : 0;
    };
    const getScheduleId = (r: any) => String(r?.scheduleId ?? r?.schedule_id ?? '').trim();
    const getItemType = (r: any) => String(r?.itemType ?? r?.item_type ?? '').trim();
    const getSourceRunId = (r: any) => String(r?.sourceRunId ?? r?.source_run_id ?? '').trim();
    const getRunId = (r: any) => String(r?.runId ?? r?.run_id ?? '').trim();

    for (const row of scheduleRuns) {
      const itemType = getItemType(row);
      if (itemType !== 'scheduled_search_page') continue;
      const scheduleId = getScheduleId(row);
      const sourceRunId = getSourceRunId(row) || getRunId(row).split(':page:')[0];
      if (!scheduleId || !sourceRunId) continue;
      const updatedAt = Math.max(parseTime(row?.updatedAt ?? row?.updated_at), parseTime(row?.createdAt ?? row?.created_at));
      const entry = byParent.get(sourceRunId) ?? { scheduleId, updatedAt: 0, pages: [] };
      entry.scheduleId = scheduleId || entry.scheduleId;
      entry.updatedAt = Math.max(entry.updatedAt, updatedAt);
      entry.pages.push(row);
      byParent.set(sourceRunId, entry);
    }

    const latestBySchedule = new Map<string, { updatedAt: number; pages: any[] }>();
    for (const entry of byParent.values()) {
      const existing = latestBySchedule.get(entry.scheduleId);
      if (!existing || entry.updatedAt > existing.updatedAt) {
        latestBySchedule.set(entry.scheduleId, { updatedAt: entry.updatedAt, pages: entry.pages });
      }
    }

    for (const [scheduleId, run] of latestBySchedule.entries()) {
      const pages = run.pages
        .map((p) => Number(p?.page))
        .filter((n) => Number.isFinite(n));
      const lastPage = pages.length ? Math.max(...pages) : null;
      const totalPages = run.pages.length || null;
      const creditsUsed = run.pages.reduce((sum, p) => sum + Number(p?.researchTriggered ?? p?.research_triggered ?? 0), 0);
      metrics.set(scheduleId, { lastPage, totalPages, creditsUsed });
    }
    return metrics;
  }, [scheduleRuns]);

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Account Settings</h1>
          <p className="text-sm text-gray-400 mt-1">Manage your profile, password, credits, and integration health.</p>
        </div>
        <div className="flex items-center gap-2 border-b border-border-dark pb-2">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-3 py-1.5 rounded text-sm ${
              activeTab === 'settings' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Settings
          </button>
          <div className="text-xs text-gray-500">Instantly analytics moved to the `Email` menu.</div>
        </div>

        {loading ? <div className="text-gray-400">Loading...</div> : null}
        {error ? <div className="rounded-lg border border-red-700 bg-red-900/30 px-4 py-3 text-sm text-red-200">{error}</div> : null}
        {success ? <div className="rounded-lg border border-emerald-700 bg-emerald-900/30 px-4 py-3 text-sm text-emerald-200">{success}</div> : null}

        {!loading && account && activeTab === 'settings' && (
          <>
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-xl border border-gray-800 bg-card-dark p-5 space-y-4">
                <h2 className="text-lg font-semibold">Profile</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First Name" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white" />
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last Name" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white" />
                </div>
                <input value={account.email} disabled className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-gray-400" />
                <button onClick={onSaveProfile} disabled={savingProfile} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50">
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>

              <div className="rounded-xl border border-gray-800 bg-card-dark p-5 space-y-4">
                <h2 className="text-lg font-semibold">Security</h2>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current Password" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white" />
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white" />
                <button onClick={onChangePassword} disabled={changingPassword} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-semibold disabled:opacity-50">
                  {changingPassword ? 'Updating...' : 'Change Password'}
                </button>
              </div>
            </section>

            <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">My GHL Connection</h3>
                  <p className="text-xs text-gray-500">Single 1:1 GHL account and quick test payload runner.</p>
                </div>
              </div>
              {ghlStatus && <div className="text-xs text-gray-400">{ghlStatus}</div>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  value={ghlAccountName}
                  onChange={(e) => setGhlAccountName(e.target.value)}
                  placeholder="Account Name"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  value={ghlLocationId}
                  onChange={(e) => setGhlLocationId(e.target.value)}
                  placeholder="Location ID"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  value={ghlPit}
                  onChange={(e) => setGhlPit(e.target.value)}
                  placeholder="PIT"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onSaveGhlConnection}
                  disabled={ghlSaving}
                  className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
                >
                  {ghlSaving ? 'Saving…' : 'Save Account'}
                </button>
                <button
                  onClick={() => {
                    setGhlAccountName('');
                    setGhlLocationId('');
                    setGhlPit('');
                    setGhlTestResultJson('');
                    setGhlStatus(null);
                  }}
                  className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white text-xs"
                >
                  Clear
                </button>
              </div>
              <div className="border-t border-gray-800 pt-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold">Test Payload</h4>
                  <button
                    onClick={onTestGhlConnection}
                    disabled={ghlTesting}
                    className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
                  >
                    {ghlTesting ? 'Testing…' : 'Test Current Inputs'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    value={ghlTestContactName}
                    onChange={(e) => setGhlTestContactName(e.target.value)}
                    placeholder="Contact Name"
                    className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                  <input
                    value={ghlTestContactEmail}
                    onChange={(e) => setGhlTestContactEmail(e.target.value)}
                    placeholder="Email"
                    className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                  <input
                    value={ghlTestCompanyName}
                    onChange={(e) => setGhlTestCompanyName(e.target.value)}
                    placeholder="Company Name"
                    className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">
{JSON.stringify(
  {
    locationId: ghlLocationId.trim() || '<set location id>',
    firstName: (ghlTestContactName.trim().split(/\s+/).filter(Boolean)[0] || 'Profitibull'),
    lastName: (ghlTestContactName.trim().split(/\s+/).filter(Boolean).slice(1).join(' ') || 'Support'),
    name: ghlTestContactName.trim() || 'Profitibull Support',
    email: ghlTestContactEmail.trim() || 'support@profitibull.com',
    companyName: ghlTestCompanyName.trim() || 'Profitibull',
  },
  null,
  2
)}
                </pre>
                {ghlTestResultJson && (
                  <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">
                    {ghlTestResultJson}
                  </pre>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-gray-800 bg-card-dark p-5">
              <h2 className="text-lg font-semibold mb-3">Credits</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3">
                  <div className="text-gray-400">Current Credits</div>
                  <div className="text-xl font-bold text-white">{account.credits}</div>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3">
                  <div className="text-gray-400">Contact Credits</div>
                  <div className="text-xl font-bold text-white">{account.contactCredits}</div>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3">
                  <div className="text-gray-400">Resupply Day</div>
                  <div className="text-xl font-bold text-white">1st</div>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3">
                  <div className="text-gray-400">Next Resupply</div>
                  <div className="text-sm font-semibold text-white">{formatDate(account.nextResupplyAt)}</div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-800 bg-card-dark p-5">
              <h2 className="text-lg font-semibold mb-3">Scheduled Searches</h2>
              {sortedSchedules.length === 0 ? (
                <div className="text-sm text-gray-500">No scheduled searches.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-gray-400 text-left">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Schedule</th>
                        <th className="py-2 pr-4">Timezone</th>
                        <th className="py-2 pr-4">Next Run</th>
                        <th className="py-2 pr-4">page_current_lastPage</th>
                        <th className="py-2 pr-4">total_pages</th>
                        <th className="py-2 pr-4">credits_used</th>
                        <th className="py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSchedules.map((s) => (
                        (() => {
                          const m = scheduleMetrics.get(String(s.scheduleId ?? '').trim());
                          const pageCurrentLastPage = m?.lastPage ?? (Number.isFinite(Number((s as any).lastPage)) ? Number((s as any).lastPage) : null);
                          return (
                        <tr key={s.scheduleId || s.name} className="border-b border-gray-900">
                          <td className="py-2 pr-4 text-white">{s.name || '—'}</td>
                          <td className="py-2 pr-4 text-gray-300">{s.status || 'ACTIVE'}</td>
                          <td className="py-2 pr-4 text-gray-300">
                            {(s.scheduleDays || []).join(', ')} @ {formatHour(Number(s.scheduleHour || 0), Number((s as any).scheduleMinute ?? 0))}
                          </td>
                          <td className="py-2 pr-4 text-gray-300">{s.timezone || '—'}</td>
                          <td className="py-2 pr-4 text-gray-300">{formatDate(s.nextRunAt)}</td>
                          <td className="py-2 pr-4 text-gray-300">{pageCurrentLastPage ?? '—'}</td>
                          <td className="py-2 pr-4 text-gray-300">{m?.totalPages ?? '—'}</td>
                          <td className="py-2 pr-4 text-gray-300">{m?.creditsUsed ?? '—'}</td>
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              <button
                                disabled={scheduleActionLoadingId === String(s.scheduleId ?? '')}
                                className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                                onClick={async () => {
                                  const scheduleId = String(s.scheduleId ?? '').trim();
                                  if (!scheduleId || !userEmail || !adminToken) return;
                                  setScheduleActionLoadingId(scheduleId);
                                  setError('');
                                  setSuccess('');
                                  try {
                                    const res = await updateMySchedule({
                                      email: userEmail,
                                      token: adminToken,
                                      scheduleId,
                                      updates: { enabled: !(s as any).enabled },
                                    });
                                    if (!res.success) throw new Error(res.error || 'Failed to update schedule');
                                    const fresh = await getAccountSettings({ token: adminToken, email: userEmail });
                                    if (fresh.success) setSchedules(fresh.schedules || []);
                                    setSuccess(`Schedule ${!(s as any).enabled ? 'enabled' : 'disabled'}.`);
                                  } catch (e: any) {
                                    setError(e?.message || 'Failed to update schedule');
                                  } finally {
                                    setScheduleActionLoadingId(null);
                                  }
                                }}
                              >
                                {(s as any).enabled ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                disabled={scheduleActionLoadingId === String(s.scheduleId ?? '')}
                                className="text-xs px-2 py-1 rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-50"
                                onClick={async () => {
                                  const scheduleId = String(s.scheduleId ?? '').trim();
                                  if (!scheduleId || !userEmail || !adminToken) return;
                                  if (!confirm('Delete this schedule?')) return;
                                  setScheduleActionLoadingId(scheduleId);
                                  setError('');
                                  setSuccess('');
                                  try {
                                    const res = await deleteMySchedule({
                                      email: userEmail,
                                      token: adminToken,
                                      scheduleId,
                                    });
                                    if (!res.success) throw new Error(res.error || 'Failed to delete schedule');
                                    const fresh = await getAccountSettings({ token: adminToken, email: userEmail });
                                    if (fresh.success) setSchedules(fresh.schedules || []);
                                    setSuccess('Schedule deleted.');
                                  } catch (e: any) {
                                    setError(e?.message || 'Failed to delete schedule');
                                  } finally {
                                    setScheduleActionLoadingId(null);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                          );
                        })()
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="rounded-xl border border-gray-800 bg-card-dark p-5 space-y-3">
              <h2 className="text-lg font-semibold">Integration Health (Read-only)</h2>
              <IntegrationRow label="Webhook URL" value={integrations.webhook} />
              <IntegrationRow label="PIT Token" value={integrations.pitToken} />
              <IntegrationRow label="Location ID" value={integrations.locationId} />
              <IntegrationRow label="LinkedIn Connection Cookie" value={integrations.linkedInConnectionCookie} />
              <IntegrationRow label="Instagram Connection Cookie" value={integrations.instagramConnectionCookie} />
              <IntegrationRow label="LinkedIn Comment Agent ID" value={integrations.linkedInCommentAgentId} />
              <IntegrationRow label="Instagram Comment Agent ID" value={integrations.instagramCommentAgentId} />
            </section>
          </>
        )}

        {!loading && account && activeTab === 'email_data' && hasEmailDataAccess && (
          <div className="space-y-4">
            <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold">Instantly Email Data</h3>
                  <p className="text-xs text-gray-500">
                    Same reporting surface with campaign options limited to assignments from Command Center.
                  </p>
                </div>
                <div className="text-xs text-gray-400">
                  Assigned campaigns: <span className="text-white font-semibold">{assignedCampaignIds.length}</span>
                </div>
              </div>
              {emailDataStatus && <div className="text-xs text-gray-400">{emailDataStatus}</div>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <select
                  value={emailFilterCampaignId}
                  onChange={(e) => setEmailFilterCampaignId(e.target.value)}
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                >
                  {emailCampaignOptions.map((campaign) => (
                    <option key={`account-email-campaign-${campaign.id}`} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
                <input
                  value={emailFilterSenderEmail}
                  onChange={(e) => setEmailFilterSenderEmail(e.target.value)}
                  placeholder="Sender Email (optional)"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  value={emailLimit}
                  onChange={(e) => setEmailLimit(e.target.value)}
                  placeholder="Limit (max 50000)"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  type="date"
                  value={emailFilterStart}
                  onChange={(e) => setEmailFilterStart(e.target.value)}
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  type="date"
                  value={emailFilterEnd}
                  onChange={(e) => setEmailFilterEnd(e.target.value)}
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    void loadEmailData();
                  }}
                  disabled={emailDataLoading || !emailFilterCampaignId}
                  className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
                >
                  {emailDataLoading ? 'Loading…' : 'Run Report'}
                </button>
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Sends</div>
                <div className="text-white text-base font-semibold">{Number(emailTotals.sends ?? 0).toLocaleString()}</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Opens</div>
                <div className="text-white text-base font-semibold">{Number(emailTotals.opens ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {emailOpenRate.toFixed(2)}% open rate | {Number(emailUniqueTotals.unique_opens ?? 0).toLocaleString()} unique
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Clicks</div>
                <div className="text-white text-base font-semibold">{Number(emailTotals.clicks ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {emailClickRate.toFixed(2)}% click rate | {Number(emailUniqueTotals.unique_clicks ?? 0).toLocaleString()} unique
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Replies</div>
                <div className="text-white text-base font-semibold">{Number(emailTotals.replies ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {emailReplyRate.toFixed(2)}% reply rate | {Number(emailUniqueTotals.unique_replies ?? 0).toLocaleString()} unique
                </div>
              </div>
            </section>

            <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
              <div className="text-xs text-gray-400 mb-2">Campaign Performance</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Campaign</th>
                    <th className="py-2 pr-4">Campaign ID</th>
                    <th className="py-2 pr-4">Sends</th>
                    <th className="py-2 pr-4">Unique Opens</th>
                    <th className="py-2 pr-4">Unique Clicks</th>
                    <th className="py-2 pr-4">Unique Replies</th>
                    <th className="py-2 pr-4">Bounces</th>
                    <th className="py-2 pr-4">Unsubs</th>
                  </tr>
                </thead>
                <tbody>
                  {emailTopCampaigns.map((row) => (
                    <tr key={`${row.campaign_id}-${row.campaign_name || ''}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.campaign_name || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.campaign_id || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_opens ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_clicks ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_replies ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unsubscribes ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {emailTopCampaigns.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={8}>
                        No campaign analytics found for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
              <div className="text-xs text-gray-400 mb-2">Sender Performance</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Sender</th>
                    <th className="py-2 pr-4">Sends</th>
                    <th className="py-2 pr-4">Opens</th>
                    <th className="py-2 pr-4">Clicks</th>
                    <th className="py-2 pr-4">Replies</th>
                    <th className="py-2 pr-4">Bounces</th>
                  </tr>
                </thead>
                <tbody>
                  {emailTopSenders.map((row) => (
                    <tr key={`account-sender-${row.sender_email}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.sender_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.opens ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.clicks ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.replies ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {emailTopSenders.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={6}>
                        No sender analytics available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
              <div className="text-xs text-gray-400 mb-2">Reply Inbox</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Campaign</th>
                    <th className="py-2 pr-4">Lead Name</th>
                    <th className="py-2 pr-4">Lead Email</th>
                    <th className="py-2 pr-4">Sender</th>
                    <th className="py-2 pr-4">Replies</th>
                    <th className="py-2 pr-4">Interest</th>
                    <th className="py-2 pr-4">Last Reply</th>
                  </tr>
                </thead>
                <tbody>
                  {emailPositiveReplies.map((row) => (
                    <tr key={`account-reply-${row.reply_key}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.campaign_name || row.campaign_id || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.lead_name || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.lead_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.sender_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.reply_count ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.lt_interest_status ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.last_reply_at ? new Date(row.last_reply_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                  {emailPositiveReplies.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={7}>
                        No replies available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
              <div className="text-xs text-gray-400 mb-2">Daily Activity</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Day</th>
                    <th className="py-2 pr-4">Sends</th>
                    <th className="py-2 pr-4">Opens</th>
                    <th className="py-2 pr-4">Clicks</th>
                    <th className="py-2 pr-4">Replies</th>
                  </tr>
                </thead>
                <tbody>
                  {emailDayRows.map((row) => (
                    <tr key={`account-day-${row.day}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-300">{row.day || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.opens ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.clicks ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.replies ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {emailDayRows.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={5}>
                        No day-level analytics available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
